import React, { useState, useEffect, useCallback } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
} from "@mui/material";
import axios from "axios";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProjectTracker from "./ProjectTracker";
import "./showprojects.css";
import { Link } from "react-router-dom";
import socket from "../../socket";
import UniversalDetailsPopup from "../../component/ui/UniversalDetailsPopup";

// Column configuration – controls header, body, sorting, visibility
const columnDefs = [
  { key: "UserName", label: "User Name" },
  { key: "ProjectName", label: "Project Name" },
  { key: "Location", label: "Location" },
  { key: "Consultant", label: "Consultant" },
  { key: "Contractor", label: "Contractor" },
  { key: "ProjectType", label: "Project Type" },
  { key: "QuotedValue", label: "Quoted Value" },
  { key: "QuotedDate", label: "Quoted Date" },
  { key: "FollowupDate", label: "Followup Date" },
  { key: "ProjectStatus", label: "Project Status" },
  { key: "MakeList", label: "Make List" },
  { key: "Specifications", label: "Specifications" },
  { key: "Person", label: "Person" },
  { key: "Amount", label: "Amount" },
  { key: "Status", label: "Status" },
  { key: "Comment", label: "Comment" },
];

const COLUMN_WIDTHS_KEY = "tableColumnWidths";

const getStoredColumnWidths = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(COLUMN_WIDTHS_KEY));
    if (!Array.isArray(stored)) {
      return columnDefs.map(() => 200);
    }
    if (stored.length < columnDefs.length) {
      return [
        ...stored,
        ...Array(columnDefs.length - stored.length).fill(200),
      ];
    }
    return stored;
  } catch {
    return columnDefs.map(() => 200);
  }
};

const storeColumnWidths = (widths) => {
  localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(widths));
};

const getInitialVisibleColumns = () => {
  const savedColumns = localStorage.getItem("visibleColumns");
  return savedColumns
    ? JSON.parse(savedColumns)
    : {
      UserName: true,
      ProjectName: true,
      Location: true,
      Consultant: true,
      Contractor: true,
      ProjectType: true,
      QuotedValue: true,
      QuotedDate: true,
      FollowupDate: true,
      ProjectStatus: true,
      MakeList: true,
      Specifications: true,
      Person: true,
      Amount: true,
      Status: true,
      Comment: true,
    };
};

const ProjectTable = () => {
  const [columnWidths, setColumnWidths] = useState(getStoredColumnWidths);
  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const getInitialLimit = () => {
    const savedLimit = localStorage.getItem("itemsPerPage");
    return savedLimit ? Number(savedLimit) : 10;
  };

  const [limit, setLimit] = useState(getInitialLimit());
  const [filters, setFilters] = useState({
    consultant: "",
    contractor: "",
    projectName: "",
    location: "",
    userName: "",
    projectType: "",
    projectStatus: "",
  });

  const [user, setUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [allowedColumns, setAllowedColumns] = useState([]);

  const [showPopup, setShowPopup] = useState(false);
  const [showPopupColumn, setShowPopupColumn] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showImportExportPopup, setShowImportExportPopup] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: "updatedAt",
    direction: "desc",
  });

  // Popup for row details
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const userRole = user?.Role || "";
  const userId = user?._id;

  // ---------- Sorting ----------

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  const sortedRows = [...filteredRows].sort((a, b) => {
    const key = sortConfig.key;

    // 🔥 Default: always show recently updated first
    if (!key) {
      const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    }

    let aVal = a[key];
    let bVal = b[key];

    // Handle dates safely
    if (
      key.toLowerCase().includes("date") ||
      key === "updatedAt" ||
      key === "createdAt"
    ) {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    } else if (!isNaN(aVal) && !isNaN(bVal)) {
      aVal = Number(aVal);
      bVal = Number(bVal);
    } else {
      aVal = (aVal || "").toString().toLowerCase();
      bVal = (bVal || "").toString().toLowerCase();
    }

    return sortConfig.direction === "asc"
      ? aVal > bVal ? 1 : -1
      : aVal < bVal ? 1 : -1;
  });



  // ---------- Column resize from header border ----------

  const initResize = (e, colIndex) => {
    e.preventDefault();

    const startX = e.clientX;
    const startWidth = columnWidths[colIndex];

    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(
        startWidth + (moveEvent.clientX - startX),
        50
      );

      setColumnWidths((prev) => {
        const updated = [...prev];
        updated[colIndex] = newWidth;
        storeColumnWidths(updated);
        return updated;
      });
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const ColumnResizer = ({ index }) => (
    <div
      onMouseDown={(e) => initResize(e, index)}
      style={{
        position: "absolute",
        right: 0,
        top: 0,
        width: 6,
        height: "100%",
        cursor: "col-resize",
        userSelect: "none",
        zIndex: 2,
      }}
    />
  );

  // ---------- Helpers ----------

  const getUserIdFromUrl = () => {
    const url = window.location.href;
    return url.substring(url.lastIndexOf("/") + 1);
  };

  const applyFilters = useCallback(
  (project) => {
    if (!project) return false;

    const normalize = (val) =>
      (val || "").toString().trim().toLowerCase();

    const consultant = normalize(filters.consultant);
    const contractor = normalize(filters.contractor);
    const projectName = normalize(filters.projectName);
    const location = normalize(filters.location);
    const userName = normalize(filters.userName);
    const projectStatus = normalize(filters.projectStatus);
    const projectType = normalize(filters.projectType);

    const projectTypeValue = normalize(project.ProjectType);

    // ✅ SPECIAL LOGIC FOR "OTHERS"
    const matchesProjectType =
      !projectType ||
      (
        projectType === "others"
          ? projectTypeValue.startsWith("other-")
          : projectTypeValue === projectType ||
            projectTypeValue.includes(projectType)
      );

    const matchesFilters =
      (!consultant ||
        normalize(project.Consultant).includes(consultant)) &&
      (!contractor ||
        normalize(project.Contractor).includes(contractor)) &&
      (!projectName ||
        normalize(project.ProjectName).includes(projectName)) &&
      (!location ||
        normalize(project.Location).includes(location)) &&
      (!userName ||
        normalize(project.UserName).includes(userName)) &&
      (!projectStatus ||
        normalize(project.ProjectStatus).includes(projectStatus)) &&
      matchesProjectType;

    const canSeeProject =
      !user?.PermissiontoSee ||
      user.PermissiontoSee.includes(project.CreatedBy);

    return matchesFilters && canSeeProject;
  },
  [filters, user]
);


  const fetchUserData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}projectusers`
      );

      const currentUser = response.data.allUsers.find(
        (u) => u._id === getUserIdFromUrl()
      );
      setUser(currentUser);

      const sortedAllowed = (currentUser?.AllowedColumns || []).sort(
        (a, b) =>
          columnDefs.findIndex((col) => col.key === a) -
          columnDefs.findIndex((col) => col.key === b)
      );

      setAllowedColumns(sortedAllowed);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, []);

  const filtersApplied = () =>
    Object.values(filters).some(
      (value) => (value || "").toString().trim() !== ""
    );

  const fetchData = useCallback(async () => {
    try {
      const params = {
        ...filters,
        page: filtersApplied() ? 1 : currentPage,
        limit,
        userId: getUserIdFromUrl(),
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}show-projects`,
        {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      const projectsArray = Array.isArray(response.data.projects)
        ? response.data.projects
        : [];

      setRows(projectsArray);
      setFilteredRows(projectsArray.filter(applyFilters));
      setTotalPages(response.data.totalPages || 1);

      if (filtersApplied()) setCurrentPage(1);
    } catch (error) {
      toast.error("Error fetching data");
      console.error("Error fetching data:", error.response?.data || error);
    }
  }, [filters, currentPage, limit, applyFilters]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    fetchData();

    socket.on("update_data", () => {
      fetchData();
    });

    return () => {
      socket.off("update_data");
    };
  }, [fetchData]);

  useEffect(() => {
    if (Array.isArray(rows)) {
      setFilteredRows(rows.filter(applyFilters));
    } else {
      setFilteredRows([]);
    }
  }, [rows, filters, applyFilters]);

  // ---------- LocalStorage for visible columns ----------

  useEffect(() => {
    localStorage.setItem("visibleColumns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // ---------- Handlers ----------

  const handleLimitChange = (event) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    localStorage.setItem("itemsPerPage", newLimit);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      const isConfirmed = window.confirm(
        "Do you really want to delete this project?"
      );
      if (!isConfirmed) return;

      await axios.delete(
        `${process.env.REACT_APP_API_URL}delete-project/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      setRows((prevRows) => prevRows.filter((row) => row && row._id !== id));
      setBulkSelectedIds((prev) => prev.filter((pid) => pid !== id));

      toast.success("Project deleted successfully");
    } catch (error) {
      toast.error("Error deleting project");
    }
  };

  // Popup row click
  const handleRowClick = (row) => {
    if (!row) return;
    setSelectedRow(row);
    setDetailsOpen(true);
  };

  const handlePopupClose = () => {
    setDetailsOpen(false);
    setSelectedRow(null);
  };

  const handlePopupSave = async (updatedData) => {
    if (!updatedData?._id) return;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}update-project/${updatedData._id}`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      const updatedProject = response.data.updatedProject || updatedData;

      setRows((prevRows) =>
        prevRows.map((row) =>
          row && row._id === updatedProject._id
            ? { ...row, ...updatedProject }
            : row
        )
      );

      socket.emit("projectUpdated", updatedProject);
      toast.success("Project updated successfully");
      handlePopupClose();
      console.log(updatedProject)
    } catch (error) {
      toast.error("Error updating project");
      console.error("Error updating project:", error);
    }
  };

  const handlePopupDelete = async (id) => {
    await handleDelete(id);
    handlePopupClose();
  };

  useEffect(() => {
    const handleProjectUpdate = (updatedProject) => {
      if (!updatedProject?._id) return;
      setRows((prevRows) =>
        prevRows.map((row) =>
          row && row._id === updatedProject._id
            ? { ...row, ...updatedProject }
            : row
        )
      );
    };

    socket.on("projectUpdated", handleProjectUpdate);
    return () => {
      socket.off("projectUpdated", handleProjectUpdate);
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "dd-MM-yyyy");
  };

  const exportToExcel = () => {
    const data = filteredRows
      .filter(Boolean)
      .map((row) => ({
        "User Name": row.UserName,
        "Project Name": row.ProjectName,
        Location: row.Location,
        Consultant: row.Consultant,
        Contractor: row.Contractor,
        "Project Type": row.ProjectType,
        "Quoted Value": row.QuotedValue,
        "Quoted Date": row.QuotedDate ? formatDate(row.QuotedDate) : "",
        "Followup Date": row.FollowupDate ? formatDate(row.FollowupDate) : "",
        "Project Status": row.ProjectStatus,
        "Make List": row.MakeList,
        Specifications: row.Specifications,
        Person: row.Person,
        Amount: row.Amount,
        Status: row.Status,
        Comment: row.Comment,
      }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projects");
    XLSX.writeFile(wb, "Projects.xlsx");
  };

  const importFromExcel = () => {
    if (!selectedFile) {
      toast.error("No file selected");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("userId", getUserIdFromUrl());

    axios
      .post(`${process.env.REACT_APP_API_URL}import-projects`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      })
      .then((response) => {
        if (Array.isArray(response.data.projects)) {
          const newProjects = response.data.projects.filter(Boolean);
          setRows((prevRows) => [...prevRows, ...newProjects]);
          setSelectedFile(null);
          toast.success("File imported successfully");
        } else {
          toast.error("Error importing file");
        }
      })
      .catch((error) => {
        console.error("Error importing file:", error);
        toast.error("Error importing file");
      });
  };

  const resetFilters = () => {
    setFilters({
      consultant: "",
      contractor: "",
      projectName: "",
      location: "",
      userName: "",
      projectStatus: "",
      projectType: "",
    });
    setCurrentPage(1);
    fetchData();
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0] || null);
  };

  const handleAddEmployee = () => {
    setShowPopup(true);
  };

  const handleCloseAddPopup = () => {
    setShowPopup(false);
  };

  const handleClosePopupcolumn = () => {
    setShowPopupColumn(false);
  };

  const handleShowPopupcolumn = () => {
    setShowPopupColumn(true);
  };

  const handleShowImportExportPopup = () => {
    setShowImportExportPopup(true);
  };

  const handleCloseImportExportPopup = () => {
    setShowImportExportPopup(false);
  };

  const handleBulkDelete = async () => {
    try {
      const isConfirmed = window.confirm(
        "Do you really want to delete the selected projects?"
      );
      if (!isConfirmed) return;

      const idsToDelete = bulkSelectedIds.filter(Boolean);

      await Promise.all(
        idsToDelete.map((id) =>
          axios.delete(
            `${process.env.REACT_APP_API_URL}delete-project/${id}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("jwt")}`,
              },
            }
          )
        )
      );

      setRows((prev) =>
        prev.filter((project) => project && !idsToDelete.includes(project._id))
      );
      setBulkSelectedIds([]);

      toast.success("Selected projects deleted successfully");
    } catch (error) {
      console.error("Error deleting selected projects:", error);
      toast.error("Error deleting selected projects");
    }
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const handleArchiveSelected = async () => {
    try {
      const idsToArchive = bulkSelectedIds.filter(Boolean);

      await Promise.all(
        idsToArchive.map((id) =>
          axios.post(`${API_BASE_URL}archive-project/${id}`, { userId })
        )
      );
      setRows((prev) =>
        prev.filter((project) => project && !idsToArchive.includes(project._id))
      );
      setBulkSelectedIds([]);
      toast.success("Selected projects Archived successfully");
    } catch (err) {
      console.error("Error archiving selected projects:", err);
    }
  };

  // ---------- Table cell render helpers ----------

  const renderDisplayCell = (row, colKey) => {
    if (!row) return "";

    switch (colKey) {
      case "UserName":
        return (
          <span style={{ fontSize: "15px", textTransform: "capitalize" }}>
            <strong>{row.UserName}</strong>
          </span>
        );

      case "QuotedDate":
        return row.QuotedDate ? formatDate(row.QuotedDate) : "dd-mm-yyyy";

      case "FollowupDate":
        return row.FollowupDate ? formatDate(row.FollowupDate) : "dd-mm-yyyy";

      case "MakeList":
        return row.MakeList || "";

      case "Specifications": {
        const specs = Array.isArray(row.Specifications)
          ? row.Specifications.join(", ")
          : row.Specifications || "";

        if (!specs) return "—";

        return (
          <div
            style={{
              maxWidth: "220px",
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflow: "hidden",
              display: "block",
            }}
          >
            {specs}
          </div>
        );
      }

      case "Comment": {
        const commentText = row.Comment || "";
        if (!commentText) return "—";

        return (
          <div
            style={{
              maxWidth: "220px",
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflow: "hidden",
              display: "block",
            }}
          >
            {commentText}
          </div>
        );
      }

      default:
        return row[colKey];
    }
  };

  // SR NO selection toggle
  const toggleRowSelection = (id) => {
    setBulkSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ---------- JSX ----------

  return (
    <div className="show-project-wrap">
      <div className="top-buttons">
        <div className="add-project-section">
          <div
            className="add-project-btn"
            style={{ cursor: "pointer" }}
            onClick={handleAddEmployee}
          >
            <span> + Add Project </span>
          </div>
          {showPopup && (
            <div className="popup-overlay">
              <div className="add-project-popup">
                <ProjectTracker onClose={handleCloseAddPopup} />
              </div>
            </div>
          )}
        </div>
        <div className="import-export-box">
          <Button onClick={() => setIsOpen((prev) => !prev)}>
            {isOpen ? "Hide Filters" : "Show Filters"}
          </Button>
          <Button>
            <Link to="/archive-projects">Show Archived</Link>
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleArchiveSelected}
            disabled={bulkSelectedIds.length === 0}
          >
            Archive Selected
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleBulkDelete}
            disabled={bulkSelectedIds.length === 0}
          >
            Delete Selected
          </Button>

          {showImportExportPopup && (
            <div className="popup-overlay">
              <div className="import-export-filter-popup">
                <div className="popup-header">
                  <h3>Import/Export</h3>
                  <button
                    onClick={handleCloseImportExportPopup}
                    className="close-button"
                  >
                    X
                  </button>
                </div>
                <div className="popup-content">
                  {userRole === "administrator" && (
                    <div className="import-export-section">
                      <div>
                        <Button onClick={exportToExcel}>
                          Export to Excel
                        </Button>
                      </div>
                      <div>
                        <input
                          type="file"
                          accept=".xlsx"
                          onChange={handleFileChange}
                        />
                        <Button onClick={importFromExcel}>
                          Import from Excel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <>
        <div className="project-table">
          <div className="filter-row">
            {showPopupColumn && (
              <div className="popup-overlay">
                <div className="column-filter comment-popup">
                  {columnDefs.map((column) => (
                    <label key={column.key}>
                      <input
                        type="checkbox"
                        checked={visibleColumns[column.key]}
                        onChange={() => toggleColumnVisibility(column.key)}
                      />
                      {column.label}
                    </label>
                  ))}
                  <button onClick={handleClosePopupcolumn}>Close</button>
                </div>
              </div>
            )}
          </div>

          <div className="input-filters">
            {isOpen && (
              <div className="filters-content open">
                <input
                  type="text"
                  placeholder="User Name"
                  name="userName"
                  value={filters.userName}
                  onChange={handleFilterChange}
                />

                <input
                  type="text"
                  placeholder="Project Name"
                  name="projectName"
                  value={filters.projectName}
                  onChange={handleFilterChange}
                />

                <input
                  type="text"
                  placeholder="Consultant"
                  name="consultant"
                  value={filters.consultant}
                  onChange={handleFilterChange}
                />

                <input
                  type="text"
                  placeholder="Contractor"
                  name="contractor"
                  value={filters.contractor}
                  onChange={handleFilterChange}
                />

                <select
                  name="projectType"
                  value={filters.projectType}
                  onChange={handleFilterChange}
                >
                  <option value="">Select Project Type</option>
                  <option value="Hotels">Hotels</option>
                  <option value="Hospitals">Hospitals</option>
                  <option value="Data Centers">Data Centers</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Industrial">Industrial</option>
                  <option value="Airports">Airports</option>
                  <option value="Metro/Rail Systems">Metro/Rail Systems</option>
                  <option value="Pharmaceutical">Pharmaceutical</option>
                  <option value="Educational Institutions">
                    Educational Institutions
                  </option>
                  <option value="Residential Buildings">
                    Residential Buildings
                  </option>
                  <option value="Government Projects">
                    Government Projects
                  </option>
                  <option value="IT Parks">
                    IT Parks
                  </option>
                  <option value="Others">Others</option>
                </select>

                <input
                  type="text"
                  placeholder="Location"
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                />

                <input
                  type="text"
                  placeholder="Project Status"
                  name="projectStatus"
                  value={filters.projectStatus}
                  onChange={handleFilterChange}
                />

                <button onClick={resetFilters}>Reset</button>
                <button onClick={handleShowImportExportPopup}>
                  Import / Export
                </button>
                <button onClick={handleShowPopupcolumn}>Column Filters</button>
              </div>
            )}
          </div>
        </div>

        <TableContainer className="project-grid" component={Paper}>
          <div className="project-range-box">
            <label htmlFor="limit">Items per page:</label>
            <select id="limit" value={limit} onChange={handleLimitChange}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
          </div>

          <Table sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                {/* Sr No. header (selection column) */}
                <TableCell
                  style={{
                    width: 60,
                    border: "1px solid black",
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  Sr No.
                </TableCell>

                {/* Dynamic headers with border resizer */}
                {columnDefs.map((col, index) => {
                  if (!allowedColumns.includes(col.key)) return null;
                  if (!visibleColumns[col.key]) return null;

                  return (
                    <TableCell
                      key={col.key}
                      className="table-cell"
                      style={{
                        width: columnWidths[index],
                        minWidth: columnWidths[index],
                        maxWidth: columnWidths[index],
                        border: "1px solid black",
                        fontWeight: "600",
                        position: "relative",
                        paddingRight: 8,
                        cursor: "pointer",
                      }}
                      onClick={() => handleSort(col.key)}
                    >
                      <div style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                        {col.label}{" "}
                        {sortConfig.key === col.key &&
                          (sortConfig.direction === "asc" ? "▲" : "▼")}
                      </div>
                      <ColumnResizer index={index} />
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody style={{ border: "1px solid black" }}>
              {sortedRows.filter(Boolean).map((row, idx) => {
                const isSelected = bulkSelectedIds.includes(row._id);
                const srNo = (currentPage - 1) * limit + idx + 1;

                return (
                  <TableRow
                    key={row._id}
                    style={{
                      border: "1px solid black",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "rgba(6, 84, 247, 0.31)" : "white",
                    }}
                    onClick={() => handleRowClick(row)}
                  >
                    {/* Sr No. selection cell */}
                    <TableCell
                      style={{
                        width: 50,
                        border: "1px solid black",
                        fontWeight: 600,
                        textAlign: "center",
                        userSelect: "none",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Don't open popup
                        toggleRowSelection(row._id);
                      }}
                    >
                      {srNo}
                    </TableCell>

                    {/* Dynamic body cells */}
                    {columnDefs.map((col) => {
                      if (!allowedColumns.includes(col.key)) return null;
                      if (!visibleColumns[col.key]) return null;

                      return (
                        <TableCell key={col.key} className="table-cell">
                          {renderDisplayCell(row, col.key)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.max(prev - 1, 1))
              }
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </>

      {/* Universal popup (View → Edit mode) */}
      <UniversalDetailsPopup
        open={detailsOpen}
        row={selectedRow}
        onClose={handlePopupClose}
        onSave={handlePopupSave}
        onDelete={handlePopupDelete}

        allowedColumns={allowedColumns}
        hiddenFields={["_id", "__v", "CreatedBy", "updatedAt", "createdAt"]}
        nonEditableFields={["UserName"]}

        fieldTypes={{
          QuotedDate: "date",
          FollowupDate: "date",
          Comment: "textarea",
          Specifications: "textarea",

          ProjectType: {
            type: "select",
            options: [
              "Hotels",
              "Hospitals",
              "Data Centers",
              "Pharmaceutical",
              "Commercial",
              "Industrial",
              "Airports",
              "Metro/Rail Systems",
              "Educational Institutions",
              "Residential Buildings",
              "Government Projects",
              "IT Parks",
              "Others",
            ]
          },

          MakeList: {
            type: "select",
            options: [
              "Make Approved",
              "Make Approved With Spec",
              "Make Part Approved ",
              "Not Approved",
              "Unknown"
            ]
          },

          ProjectStatus: {
            type: "select",
            options: [
              "Bidding",
              "Contractor-Won",
              "Contractor Lost",
              "Omicron Los",
              "Omicron Won",
              "Part PO Received",
              "Complete PO Received"
            ]
          },

          Status: {
            type: "select",
            options: ["N/A", "Transferd", "Pending"]
          }
        }}
      />

    </div>
  );
};

export default ProjectTable;
